import React, { useEffect, useState, useTransition } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import { toast } from "utils/toast";
import {
  RadioCardGroup,
  RadioCardItem,
  RadioCardIndicator,
} from "components/Graphs/RadioCardGroup";
import agent from "server";
import {
  LockClosedIcon,
  LinkIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/Graphs/Dialogue";
import { Button } from "components/Graphs/Button";

export type AccessLevel = "public" | "organization" | "published";

export interface LaunchableAccessControlProps {
  initialAccessLevel: AccessLevel;
  disabled?: boolean;
  launchableId?: string;
  orgId?: string;
  onChange?: (accessLevel: AccessLevel) => void;
  showAsText?: boolean;
  launchableName?: string;
  description?: string;
  readmeLink?: string;
  onPublishClick?: () => void;
}

export const LaunchableAccessControl: React.FC<
  LaunchableAccessControlProps
> = ({
  initialAccessLevel,
  disabled = false,
  launchableId,
  orgId,
  onChange,
  showAsText = false,
  launchableName,
  description,
  readmeLink,
  onPublishClick,
}) => {
  const [, startTransition] = useTransition();
  const [accessPopoverOpen, setAccessPopoverOpen] = useState(false);
  const [currentAccessLevel, setCurrentAccessLevel] =
    useState<AccessLevel>(initialAccessLevel);
  const [tempAccessLevel, setTempAccessLevel] =
    useState<AccessLevel>(initialAccessLevel);
  const [saveAccessError, setSaveAccessError] = useState<string>("");
  const [savingAccess, setSavingAccess] = useState(false);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);

  // Update state when initial level changes
  useEffect(() => {
    setCurrentAccessLevel(initialAccessLevel);
    setTempAccessLevel(initialAccessLevel);
  }, [initialAccessLevel]);

  const handleAccessPopoverOpen = () => {
    setTempAccessLevel(currentAccessLevel);
    setSaveAccessError("");
    setAccessPopoverOpen(true);
  };

  const handleSaveAccess = async () => {
    // If this is a creation flow (no launchableId), just update local state
    if (!launchableId || !orgId) {
      setCurrentAccessLevel(tempAccessLevel);
      setAccessPopoverOpen(false);
      onChange?.(tempAccessLevel);
      return;
    }

    // If changing to "published", trigger the publish callback
    if (tempAccessLevel === "published") {
      setAccessPopoverOpen(false);
      if (onPublishClick) {
        startTransition(() => {
          onPublishClick();
        });
      } else {
        toast.error("Unable to publish: publish handler not configured");
      }
      return;
    }

    // If changing FROM "published" TO something else, show confirmation
    if (currentAccessLevel === "published") {
      setAccessPopoverOpen(false);
      startTransition(() => {
        setShowUnpublishConfirm(true);
      });
      return;
    }

    // Otherwise, save to backend
    await saveAccessLevel();
  };

  const saveAccessLevel = async () => {
    if (!launchableId || !orgId) return;

    setSavingAccess(true);
    setSaveAccessError("");

    const res = await agent.Launchables.editLaunchableAccess(orgId, {
      id: launchableId,
      viewAccess: tempAccessLevel,
    });

    if (res && res.success) {
      setCurrentAccessLevel(tempAccessLevel);
      setAccessPopoverOpen(false);
      onChange?.(tempAccessLevel);

      // Show toast notification only for unpublishing (major action)
      // Changes between organization/public are routine and don't need toast
      if (currentAccessLevel === "published") {
        const unpublishMessages = {
          organization:
            "Your launchable is now visible only to your organization",
          public: "Your launchable is now visible only to those with the link",
        };

        toast.info("Launchable unpublished", {
          description:
            unpublishMessages[tempAccessLevel] ||
            "Your launchable will not longer appear in search results",
        });
      }
    } else {
      const errorMsg = res?.message || "Failed to save access level";
      setSaveAccessError(errorMsg);
      toast.error("Failed to update visibility", {
        description: errorMsg,
      });
    }
    setSavingAccess(false);
  };

  const handleCancelAccess = () => {
    setTempAccessLevel(currentAccessLevel);
    setSaveAccessError("");
    setAccessPopoverOpen(false);
  };

  const handleUnpublishConfirm = async () => {
    setShowUnpublishConfirm(false);
    await saveAccessLevel();
  };

  const handleUnpublishCancel = () => {
    setShowUnpublishConfirm(false);
    setTempAccessLevel(currentAccessLevel); // Reset temp level
  };

  if (disabled && showAsText) {
    return (
      <span className="text-sm text-gray-500 ml-3 mt-[2px]">
        {currentAccessLevel === "published"
          ? "Everyone"
          : currentAccessLevel === "public"
            ? "Anyone with the link"
            : "Only my organization"}
      </span>
    );
  }

  return (
    <>
      <Popover open={accessPopoverOpen} onOpenChange={setAccessPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            className="h-8"
            variant="secondary"
            onClick={handleAccessPopoverOpen}
            disabled={disabled}
          >
            {currentAccessLevel === "published" ? (
              <GlobeAltIcon className="h-4 w-4 mr-1.5" />
            ) : currentAccessLevel === "public" ? (
              <LinkIcon className="h-4 w-4 mr-1.5" />
            ) : (
              <LockClosedIcon className="h-4 w-4 mr-1.5" />
            )}
            <span className="mr-1">
              {currentAccessLevel === "published"
                ? "Everyone"
                : currentAccessLevel === "public"
                  ? "Anyone with the link"
                  : "Only my organization"}
            </span>
            <ChevronDownIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-sm mb-2">Launchable Access</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Control who can view and deploy this launchable
              </p>
            </div>

            <RadioCardGroup
              value={tempAccessLevel}
              onValueChange={(value) =>
                setTempAccessLevel(value as AccessLevel)
              }
            >
              <div className="space-y-2">
                <RadioCardItem
                  value="organization"
                  className="flex items-start space-x-3 p-3"
                >
                  <RadioCardIndicator />
                  <LockClosedIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      Only my organization
                    </div>
                  </div>
                </RadioCardItem>

                <RadioCardItem
                  value="public"
                  className="flex items-start space-x-3 p-3"
                >
                  <RadioCardIndicator />
                  <LinkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      Anyone with the link
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Anyone with the link can view and deploy
                    </div>
                  </div>
                </RadioCardItem>

                <RadioCardItem
                  value="published"
                  className="flex items-start space-x-3 p-3"
                >
                  <RadioCardIndicator />
                  <GlobeAltIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      Everyone (published)
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Publicly discoverable by anyone
                    </div>
                  </div>
                </RadioCardItem>
              </div>
            </RadioCardGroup>

            {saveAccessError && (
              <div className="text-red-500 text-sm p-2 bg-red-50 dark:bg-red-900/20 rounded">
                {saveAccessError}
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-2">
              <Button
                variant="secondary"
                onClick={handleCancelAccess}
                disabled={savingAccess}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveAccess}
                disabled={
                  savingAccess || tempAccessLevel === currentAccessLevel
                }
              >
                {savingAccess ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Unpublish Confirmation Dialog */}
      <Dialog open={showUnpublishConfirm} onOpenChange={handleUnpublishCancel}>
        <DialogContent className="sm:max-w-[440px] p-8">
          <DialogHeader>
            <DialogTitle className="text-primary">
              Unpublish Launchable
            </DialogTitle>
            <DialogDescription className="mt-2 text-secondary">
              This launchable will no longer appear in search results. Only
              users{" "}
              {tempAccessLevel === "organization"
                ? "in your organization"
                : "with the direct link"}{" "}
              will be able to access it.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={handleUnpublishCancel}
              disabled={savingAccess}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUnpublishConfirm}
              disabled={savingAccess}
            >
              {savingAccess ? "Updating visibility..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
